import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils';

interface AppSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: Array<string | { value: string; label: string | React.ReactNode }>;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
}

/**
 * Canonical dashboard value-selector. The dropdown panel is portal-rendered
 * directly into <body> so it is never clipped or made transparent by any
 * ancestor stacking context.
 */
export function AppSelect({ value, onChange, options, className, disabled = false, fullWidth = false }: AppSelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const normalizedOptions = options.map(opt =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt,
  );
  const selectedOption = normalizedOptions.find(opt => opt.value === value);

  // Re-measure position whenever the panel opens
  const updatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        minWidth: rect.width,
      });
    }
  };

  useEffect(() => {
    if (open) updatePosition();
  }, [open]);

  // Close on outside click — must ignore clicks inside the portalled panel
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) return;
      setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const panel = open ? (
    <div
      ref={panelRef}
      style={{
        ...menuStyle,
        backgroundColor: 'var(--color-surface-light-pro, #e9eff4)',
        zIndex: 9999,
      }}
      className="flex flex-col rounded-xl shadow-lg border border-slate-200/80 py-1 overflow-hidden"
    >
      {normalizedOptions.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => { onChange(opt.value); setOpen(false); }}
          className={cn(
            'flex items-center gap-2 px-4 py-2 text-sm font-medium text-left transition-colors w-full',
            opt.value === value
              ? 'bg-primary-brown/12 text-primary-brown font-semibold'
              : 'text-text-primary hover:bg-primary-brown/8',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  ) : null;

  return (
    <div ref={containerRef} className={cn('relative inline-flex items-center', fullWidth && 'w-full')}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
        className={cn(
          'inline-flex items-center gap-2 appearance-none bg-surface-light-pro rounded-lg pl-3 pr-2.5 py-1.5 text-sm font-medium outline-none focus:ring-1 focus:ring-highlight-dark disabled:opacity-60 select-none text-text-primary',
          fullWidth && 'w-full justify-between',
          className,
        )}
      >
        <span>{selectedOption?.label ?? value}</span>
        <ChevronDown size={14} className="text-text-secondary flex-shrink-0 ml-auto" />
      </button>

      {typeof document !== 'undefined' && createPortal(panel, document.body)}
    </div>
  );
}
